import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { App } from '@capacitor/app';
import { Platform } from '@ionic/angular';
import { IonMenu, IonRouterOutlet, IonSplitPane, MenuController } from '@ionic/angular/standalone';
import { filter } from 'rxjs';

import { Breakpoint, DeviceService } from '@core/device';
import { isShellParentRoot, ROUTES, shellParentRoute, useNav } from '@core/nav';

import { ShellHeaderComponent } from './header';
import { ShellMenuComponent } from './menu';
import { ShellStore } from './shell.store';
import { ShellTabsComponent } from './tabs';

interface ShellHistoryState {
  readonly shellHomeBoundary?: boolean;
}

@Component({
  selector: 'ml-shell-layout',
  imports: [
    IonMenu,
    IonRouterOutlet,
    IonSplitPane,
    ShellHeaderComponent,
    ShellMenuComponent,
    ShellTabsComponent,
  ],
  host: {
    class: 'ml-shell-layout',
    '[class.desktop-layout]': 'device.isDesktopUI()',
  },
  templateUrl: './shell.layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellLayoutComponent {
  protected readonly device = inject(DeviceService);
  protected readonly shellStore = inject(ShellStore);

  private readonly destroyRef = inject(DestroyRef);
  private readonly menuController = inject(MenuController);
  private readonly platform = inject(Platform);
  private readonly router = inject(Router);
  private readonly nav = useNav();

  protected readonly splitPaneWhen = `(min-width: ${Breakpoint.lg}px)`;

  constructor() {
    const subscription = this.platform.backButton.subscribeWithPriority(10, (next) =>
      this.onBack(next),
    );
    this.destroyRef.onDestroy(() => subscription.unsubscribe());

    if (!this.device.isNative()) {
      const onPopState = (): void => queueMicrotask(() => this.keepWebHomeBoundary());
      window.addEventListener('popstate', onPopState);
      const routerSubscription = this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => this.keepWebHomeBoundary());
      this.destroyRef.onDestroy(() => {
        window.removeEventListener('popstate', onPopState);
        routerSubscription.unsubscribe();
      });
      this.keepWebHomeBoundary();
    }
  }

  protected async onMenuToggle(): Promise<void> {
    if (this.device.isDesktopUI()) {
      this.shellStore.toggleIconMode();
      return;
    }
    await this.menuController.toggle('shell-menu');
  }

  private onBack(next: () => void): void {
    const parent = shellParentRoute(this.router.url);
    if (!parent) {
      next();
      return;
    }

    if (!isShellParentRoot(this.router.url)) {
      void this.nav.back(parent);
      return;
    }

    if (parent !== ROUTES.home) {
      void this.nav.rootBack(ROUTES.home, { replaceUrl: true });
      return;
    }

    if (this.device.isAndroid()) {
      void App.exitApp();
      return;
    }

    if (!this.device.isNative()) {
      return;
    }

    next();
  }

  private keepWebHomeBoundary(): void {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    const state = history.state as ShellHistoryState | null;
    if (path !== ROUTES.home || state?.shellHomeBoundary) {
      return;
    }

    history.pushState({ ...state, shellHomeBoundary: true }, '', window.location.href);
  }
}
